import React from "react";
import Image from "next/image";
import { Right, Wrong } from "../icons";
import { Logs } from "../icons-v2";
import type {
  ComparisonColumnContent,
  ComparisonSection,
  VendorBrand,
} from "./comparison-types";
import "../styles/datadog-comparison-grid.module.scss";

export type VendorComparisonGridProps = {
  className?: string;
  sections: ComparisonSection[];
  competitor: VendorBrand;
  tag?: string;
  title?: React.ReactNode;
  ariaLabel?: string;
  sectionId?: string;
  
};

const ComparisonBullets: React.FC<{
  bullets: string[];
  variant: "middleware" | "competitor";
}> = ({ bullets, variant }) => {
  const Icon = variant === "middleware" ? Right : Wrong;

  return (
    <ul className="comparison-bullets">
      {bullets.map((text) => (
        <li key={text}>
          <span className="bullet-icon" aria-hidden="true">
            <Icon />
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
};

const ComparisonColumn: React.FC<{
  content: ComparisonColumnContent;
  variant: "middleware" | "competitor";
  brandLabel: string;
}> = ({ content, variant, brandLabel }) => (
  <div
    className={`comparison-column comparison-column--${variant}`}
    data-brand-label={brandLabel}
  >
    <p className="comparison-description">{content.description}</p>
    <ComparisonBullets bullets={content.bullets} variant={variant} />
    {content.image ? (
      <Image
        src={content.image}
        alt={content.description}
        width={600}
        height={400}
      />
    ) : null}
  </div>
);

const ComparisonRow: React.FC<{
  section: ComparisonSection;
  competitorName: string;
}> = ({ section, competitorName }) => {
  const SectionIcon = section.icon ?? Logs;

  return (
    <article className="comparison-row" id={section.id}>
      <div className="comparison-row-label">
        <div className="section-icon" aria-hidden="true">
          <SectionIcon />
        </div>
        <h3>{section.title}</h3>
      </div>
      <ComparisonColumn
        content={section.middleware}
        variant="middleware"
        brandLabel="Middleware"
      />
      <ComparisonColumn
        content={section.competitor}
        variant="competitor"
        brandLabel={competitorName}
      />
    </article>
  );
};

const VendorComparisonGrid: React.FC<VendorComparisonGridProps> = ({
  className = "",
  sections,
  competitor,
  tag = "Head to Head",
  title,
  ariaLabel,
  sectionId = "know-more",
}) => {
  const resolvedTitle =
    title ?? (
      <>
        Why You <span className="text-gradient">Should Replace</span>{" "}
        {competitor.name} with <span className="text-gradient">Middleware</span>
      </>
    );

  return (
    <section
      className={`datadog-comparison-grid vendor-comparison-grid ${className}`.trim()}
      id={sectionId}
      aria-label={
        ariaLabel ?? `Middleware vs ${competitor.name} comparison`
      }
    >
      <div className="container" id="comparison-mw">
        <div className="section-tag">{tag}</div>
        <h2>{resolvedTitle}</h2>
        <div className="comparison-grid-scroll">
          <div className="comparison-sticky-header" aria-hidden="false">
            <div className="comparison-grid-columns comparison-sticky-columns">
              <div className="comparison-header-label" />
              <div className="comparison-header-logo comparison-header-logo--middleware">
                <img
                  src="/images/middleware-bg-logo.svg"
                  alt="Middleware Logo"
                  width={186}
                  height={42}
                />
              </div>
              <div className="comparison-header-logo comparison-header-logo--competitor">
                <img
                  src={competitor.logoSrc}
                  alt={competitor.logoAlt ?? competitor.name}
                  width={competitor.headerLogoWidth ?? 119}
                  height={competitor.headerLogoHeight ?? 42}
                />
              </div>
            </div>
          </div>

          <div className="comparison-rows">
            {sections.map((section) => (
              <ComparisonRow
                section={section}
                competitorName={competitor.name}
                key={section.id}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorComparisonGrid;
